<template>
  <div class="row">
    <entry-fields :tab="tab" :transition="transition" class="col-xs-12" />
    <div class="col-xs-12">
      <div class="attachment-container">
        <div class="attachments cards">
          <attachment-configurator
            v-if="configurationMode && attachments.length === 0"
            :tab="tab"
          />
          <uploader
            v-if="uploaderOptions || configurationMode"
            :options="uploaderOptions"
            :attachments="attachments"
            :attachment-mode="true"
            :can-delete="canDelete"
            :can-upload="canUpload"
            :files="files"
            :file-limit="tab.maxAttachments"
            :file-count="fileCount"
            :disabled="disabled"
            @uploading="onUploading"
            @uploadingFile="uploadingFile"
            @uploaded="uploadedFile"
            @deleted="deletedFile"
            @movedLeft="movedLeft"
            @movedRight="movedRight"
            @imageDataUrl="onImageDataUrl(...arguments)"
          >
            <template slot="btn-upload-text">
              <translation :text="lang.get('files.buttons.attachments')" />
            </template>
            <template slot="file-limit-reached-text">
              <translation :text="lang.get('files.messages.file_limit')" />
            </template>
          </uploader>
        </div>
      </div>
    </div>
    <div v-if="tab.acceptAttachmentLinks" class="col-xs-12">
      <entry-links
        :file-limit="tab.maxAttachments"
        :file-count="fileCount"
        :disabled="disabled"
      >
        <template slot="file-limit-reached-text">
          <translation :text="lang.get('files.messages.file_limit')" />
        </template>
      </entry-links>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import EntryFields from '../Fields/EntryFields';
import FileUpload from '@/lib/components/Uploader/FileUpload';
import Uploader from '@/lib/components/Uploader/Uploader';
import AttachmentConfigurator from '../Configuration/AttachmentConfigurator';
import EntryLinks from '../EntryLinks/EntryLinks';
import Translation from '@/modules/interface-text/components/Translation';

const toastr = require('toastr');

export default {
  components: {
    Uploader,
    FileUpload,
    EntryFields,
    AttachmentConfigurator,
    EntryLinks,
    Translation
  },
  inject: ['lang'],
  props: {
    tab: {
      type: Object,
      required: true
    },
    transition: {
      type: String,
      default: 'push'
    }
  },
  data() {
    return {
      tempPreviews: []
    };
  },
  computed: {
    fileCount() {
      return this.links.length + this.files.length;
    },
    attachments() {
      return this.attachmentsByTab(this.tab.id);
    },
    canDelete() {
      return !this.locks.readOnly;
    },
    canUpload() {
      return !this.locks.readOnly;
    },
    files() {
      return this.attachments.map(a => a.file);
    },
    role() {
      return this.isManager ? 'manager' : 'entrant';
    },
    uploaderOptions() {
      return this.tab.uploaderOptions;
    },
    disabled() {
      return (
        !this.tabIsVisible(this.tab) ||
        this.configurationMode ||
        !this.uploaderOptions
      );
    },
    ...mapGetters('entryForm', [
      'attachmentByFile',
      'attachmentsByTab',
      'selectedTab',
      'tabIsVisible'
    ]),
    ...mapState('entryForm', ['entry', 'isManager', 'locks', 'links']),
    ...mapState('entryFormConfiguration', ['configurationMode'])
  },
  created() {
    this.orderAttachmentsFrom1ToN();
  },
  methods: {
    deletedFile(fileId, remoteId) {
      this.deleteAttachment({
        tabId: this.tab.id,
        attachment: this.attachmentByFile(fileId)
      });
      this.orderAttachmentsFrom1ToN();
      this.deleteAttachmentRequest(remoteId);
    },
    deleteAttachmentUrl(fileId) {
      return (
        '/entry/' + this.role + '/' + this.entry.slug + '/attachment/' + fileId
      );
    },
    deleteAttachmentRequest(fileId) {
      this.$http.delete(this.deleteAttachmentUrl(fileId)).then(
        () => {
          this.orderAttachmentsRequest();
        },
        error => {
          toastr.error(
            error.response.data.message ||
              document.getElementById('alerts-generic').innerHTML
          );
        }
      );
    },
    onImageDataUrl(imageDataUrl, file) {
      this.tempPreviews[file.id] = imageDataUrl;
    },
    imageDataUrlForFile(file) {
      for (let fileId of Object.keys(this.tempPreviews)) {
        if (fileId === file.oldId) {
          return this.tempPreviews[fileId];
        }
      }
      return null;
    },
    maxOrder() {
      return this.attachments.reduce(
        (accumulator, attachment) =>
          accumulator < attachment.order ? attachment.order : accumulator,
        0
      );
    },
    movedLeft(attachment) {
      const newOrder = attachment.order - 1;
      const previousAttachment = this.attachments.find(
        a => a.order === newOrder
      );

      if (previousAttachment) {
        this.updateAttachment({
          attachment: previousAttachment,
          properties: {
            order: newOrder + 1
          }
        });
      }
      this.updateAttachment({
        attachment: attachment,
        properties: {
          order: newOrder
        }
      });

      this.sortAttachmentsByOrder({ tabId: this.tab.id });
      this.orderAttachmentsFrom1ToN();
      this.orderAttachmentsRequest();
    },
    movedRight(attachment) {
      const newOrder = attachment.order + 1;
      const nextAttachment = this.attachments.find(a => a.order === newOrder);

      if (nextAttachment) {
        this.updateAttachment({
          attachment: nextAttachment,
          properties: {
            order: newOrder - 1
          }
        });
      }
      this.updateAttachment({
        attachment: attachment,
        properties: {
          order: newOrder
        }
      });

      this.sortAttachmentsByOrder({ tabId: this.tab.id });
      this.orderAttachmentsFrom1ToN();
      this.orderAttachmentsRequest();
    },
    orderAttachmentsRequest() {
      let counter = 1;
      const orderData = this.attachments
        .map(a => a.fileId)
        .reduce((acc, fileId) => {
          acc[fileId] = counter++;
          return acc;
        }, {});

      this.orderAttachments({
        url: this.orderAttachmentsUrl(),
        data: orderData
      }).then(
        () => {},
        error => {
          toastr.error(
            error.response.data.message ||
              document.getElementById('alerts-generic').innerHTML
          );
        }
      );
    },
    orderAttachmentsUrl() {
      return (
        '/entry/' + this.role + '/' + this.entry.slug + '/attachment/order'
      );
    },
    orderAttachmentsFrom1ToN() {
      this.attachments.forEach((a, index) => {
        const nextOrder = index + 1;
        if (a.order !== nextOrder) {
          this.updateAttachment({
            attachment: a,
            properties: {
              order: nextOrder
            }
          });
        }
      });
    },
    /**
     * When uploading a new file, create temporary attachment, so we can display it.
     */
    uploadingFile(file) {
      file = Object.assign({}, file);
      this.addAttachment({
        tabId: this.tab.id,
        attachmentId: 'temp-attachment-' + file.id,
        file: file,
        fileId: file.id,
        order: this.maxOrder() + 1
      });
    },
    /**
     * When file has finished uploading, remove temp attachment and create attachment with real id.
     */
    uploadedFile(completeFileTokens, file, attachmentId) {
      if (file && attachmentId) {
        file.image = this.imageDataUrlForFile(file); // Override image urls with cached data urls for new uploads
        const attachment = this.attachmentByFile(file.oldId);
        this.updateAttachment({
          attachment: attachment,
          properties: {
            id: attachmentId,
            fileId: file.id,
            file: file
          }
        });
        this.copyAttachmentValues({
          oldId: 'temp-attachment-' + file.oldId,
          newId: attachment.id
        });

        this.orderAttachmentsFrom1ToN();
        this.orderAttachmentsRequest();
      }
    },
    onUploading(uploaderId, isUploading) {
      this.setUploaderStatus({ uploaderId, isUploading });
    },
    ...mapActions('entryForm', ['addAttachment', 'orderAttachments']),
    ...mapMutations('entryForm', [
      'copyAttachmentValues',
      'deleteAttachment',
      'sortAttachmentsByOrder',
      'updateAttachmentImage',
      'updateAttachment',
      'setUploaderStatus'
    ])
  }
};
</script>
